import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CreditCard, Download, ArrowUpRight, RefreshCw, Eye, X } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { SERVICES, SERVICE_LABEL, type ServiceType } from "@/lib/services";
import {
  SUBSCRIPTION_STATUS_META,
  RENEW_MESSAGE,
  deriveStatus,
  daysUntil,
  formatDate,
  formatMoney,
  fetchPlans,
  requestPlanChange,
  useMySubscriptions,
  type PlanRow,
  type SubscriptionRow,
} from "@/lib/subscriptions";

export const Route = createFileRoute("/_authenticated/partner/subscription")({
  head: () => ({
    meta: [
      { title: "My Subscription · Landoo Partner" },
      {
        name: "description",
        content:
          "Track your Landoo plan, service type, start and expiry dates, and renew or upgrade each service subscription.",
      },
      { property: "og:title", content: "My Subscription · Landoo Partner" },
      {
        property: "og:description",
        content: "Plan, dates and status for every Landoo service you subscribe to.",
      },
    ],
  }),
  component: SubscriptionPage,
});

type Dialog =
  | { kind: "view"; service: ServiceType; row: SubscriptionRow | null }
  | { kind: "renew" | "upgrade"; service: ServiceType; row: SubscriptionRow | null }
  | null;

function SubscriptionPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { latestByService, isLoading } = useMySubscriptions();
  const [dialog, setDialog] = useState<Dialog>(null);

  const { data: plans = [] } = useQuery({ queryKey: ["category-plans"], queryFn: fetchPlans });

  const change = useMutation({
    mutationFn: (input: {
      service: ServiceType;
      plan: PlanRow;
      cycle: "monthly" | "annual";
      intent: "renew" | "upgrade";
    }) =>
      requestPlanChange({
        partnerId: user!.id,
        serviceType: input.service,
        plan: input.plan,
        billingCycle: input.cycle,
        intent: input.intent,
      }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["partner", "subscriptions"] });
      setDialog(null);
      toast.success(
        v.intent === "renew"
          ? "Renewal requested — your plan shows as Pending until the Landoo team confirms it."
          : "Upgrade requested — your new plan shows as Pending until the Landoo team confirms it.",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const expiredServices = SERVICES.filter((s) => {
    const row = latestByService.get(s.value);
    return row && deriveStatus(row) === "expired";
  });

  return (
    <DashboardShell title="My Subscription" role="Partner" items={PARTNER_NAV}>
      <p className="text-sm text-muted-foreground">
        Every Landoo service is subscribed separately. A plan for one service never unlocks another.
      </p>

      {expiredServices.length > 0 && (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <h2 className="text-sm font-bold text-rose-900">Renew Subscription</h2>
          <p className="mt-1 text-xs text-rose-900/80">
            {RENEW_MESSAGE} Expired:{" "}
            <span className="font-semibold">
              {expiredServices.map((s) => SERVICE_LABEL[s.value]).join(", ")}
            </span>
            .
          </p>
        </div>
      )}

      {/* Mobile: cards. Desktop: table. */}
      <div className="mt-4 space-y-3 lg:hidden">
        {SERVICES.map((s) => (
          <MobileCard
            key={s.value}
            service={s.value}
            row={latestByService.get(s.value) ?? null}
            onAction={(kind) =>
              setDialog({ kind, service: s.value, row: latestByService.get(s.value) ?? null })
            }
          />
        ))}
      </div>

      <div className="mt-4 hidden overflow-hidden rounded-2xl border border-border bg-card shadow-soft lg:block">
        <table className="w-full text-left text-sm">
          <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-semibold">Current Plan</th>
              <th className="px-4 py-3 font-semibold">Service Type</th>
              <th className="px-4 py-3 font-semibold">Start Date</th>
              <th className="px-4 py-3 font-semibold">Expiry Date</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {SERVICES.map((s) => {
              const row = latestByService.get(s.value) ?? null;
              const status = row ? deriveStatus(row) : null;
              const meta = status ? SUBSCRIPTION_STATUS_META[status] : null;
              const left = daysUntil(row?.expires_at ?? null);
              return (
                <tr key={s.value} className="border-t border-border/70 align-middle">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-brown-dark">
                      {row?.plan_name ?? "No subscription"}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {row
                        ? `${formatMoney(row.amount, row.currency)} · ${row.billing_cycle}`
                        : "Not subscribed yet"}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-brown-dark">{SERVICE_LABEL[s.value]}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(row?.starts_at ?? null)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDate(row?.expires_at ?? null)}
                    {status === "active" && left != null && left <= 15 && (
                      <div className="text-[11px] font-semibold text-amber-700">
                        {left} day(s) left
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {meta ? (
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}
                      >
                        {meta.label}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <ActionButtons
                      hasRow={!!row}
                      service={s.value}
                      onAction={(kind) => setDialog({ kind, service: s.value, row })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isLoading && <p className="mt-3 text-xs text-muted-foreground">Loading subscriptions…</p>}

      {dialog && (
        <SubscriptionDialog
          dialog={dialog}
          plans={plans.filter((p) => p.category === dialog.service)}
          busy={change.isPending}
          onClose={() => setDialog(null)}
          onSubmit={(plan, cycle) =>
            change.mutate({
              service: dialog.service,
              plan,
              cycle,
              intent: dialog.kind === "upgrade" ? "upgrade" : "renew",
            })
          }
        />
      )}
    </DashboardShell>
  );
}

function ActionButtons({
  hasRow,
  service,
  onAction,
}: {
  hasRow: boolean;
  service: ServiceType;
  onAction: (kind: "view" | "renew" | "upgrade") => void;
}) {
  if (!hasRow) {
    return (
      <div className="flex justify-end">
        <Link
          to="/partner/services/$serviceType"
          params={{ serviceType: service }}
          className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-[11px] font-semibold text-brown-dark shadow-soft"
        >
          Activate service
        </Link>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button
        type="button"
        onClick={() => onAction("upgrade")}
        className="inline-flex items-center gap-1 rounded-full bg-brand px-3 py-1.5 text-[11px] font-semibold text-brown-dark shadow-soft"
      >
        <ArrowUpRight className="h-3.5 w-3.5" /> Upgrade
      </button>
      <button
        type="button"
        onClick={() => onAction("renew")}
        className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-[11px] font-semibold text-brown-dark"
      >
        <RefreshCw className="h-3.5 w-3.5" /> Renew
      </button>
      <button
        type="button"
        onClick={() => onAction("view")}
        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-[11px] font-semibold text-brown-dark"
      >
        <Eye className="h-3.5 w-3.5" /> View plan
      </button>
      <button
        type="button"
        onClick={() => toast.info("Invoice download will be available once billing goes live.")}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1.5 text-[11px] font-semibold text-muted-foreground"
      >
        <Download className="h-3.5 w-3.5" /> Invoice
      </button>
    </div>
  );
}

function MobileCard({
  service,
  row,
  onAction,
}: {
  service: ServiceType;
  row: SubscriptionRow | null;
  onAction: (kind: "view" | "renew" | "upgrade") => void;
}) {
  const status = row ? deriveStatus(row) : null;
  const meta = status ? SUBSCRIPTION_STATUS_META[status] : null;
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-brown-dark">
            <CreditCard className="h-4 w-4" /> {SERVICE_LABEL[service]}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {row?.plan_name ?? "No subscription"}
          </div>
        </div>
        {meta && (
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${meta.cls}`}>
            {meta.label}
          </span>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <dt className="text-muted-foreground">Start date</dt>
          <dd className="font-semibold text-brown-dark">{formatDate(row?.starts_at ?? null)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Expiry date</dt>
          <dd className="font-semibold text-brown-dark">{formatDate(row?.expires_at ?? null)}</dd>
        </div>
      </dl>
      {meta && <p className="mt-2 text-[11px] text-muted-foreground">{meta.hint}</p>}
      <div className="mt-3">
        <ActionButtons hasRow={!!row} service={service} onAction={onAction} />
      </div>
    </div>
  );
}

function SubscriptionDialog({
  dialog,
  plans,
  busy,
  onClose,
  onSubmit,
}: {
  dialog: NonNullable<Dialog>;
  plans: PlanRow[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (plan: PlanRow, cycle: "monthly" | "annual") => void;
}) {
  const row = dialog.row;
  const [planId, setPlanId] = useState<string>(
    plans.find((p) => p.plan_key === row?.plan_key)?.id ?? plans[0]?.id ?? "",
  );
  const [cycle, setCycle] = useState<"monthly" | "annual">(
    row?.billing_cycle === "annual" ? "annual" : "monthly",
  );
  const selected = plans.find((p) => p.id === planId) ?? null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="max-h-[90vh] w-full overflow-y-auto rounded-t-3xl bg-card p-5 shadow-soft sm:max-w-lg sm:rounded-2xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-base font-bold text-brown-dark">
            {dialog.kind === "view"
              ? "Plan details"
              : dialog.kind === "renew"
                ? "Renew plan"
                : "Upgrade plan"}{" "}
            · {SERVICE_LABEL[dialog.service]}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {dialog.kind === "view" ? (
          <div className="mt-4 space-y-2 text-sm">
            <Detail label="Current plan" value={row?.plan_name ?? "No subscription"} />
            <Detail label="Service type" value={SERVICE_LABEL[dialog.service]} />
            <Detail label="Billing cycle" value={row?.billing_cycle ?? "—"} />
            <Detail label="Amount" value={row ? formatMoney(row.amount, row.currency) : "—"} />
            <Detail label="Start date" value={formatDate(row?.starts_at ?? null)} />
            <Detail label="Expiry date" value={formatDate(row?.expires_at ?? null)} />
            <Detail
              label="Status"
              value={row ? SUBSCRIPTION_STATUS_META[deriveStatus(row)].label : "—"}
            />
            {selected && (
              <div className="rounded-xl bg-muted/50 p-3 text-[11px] text-muted-foreground">
                Listing limit: {selected.listing_limit ?? "Unlimited"} · Images per listing:{" "}
                {selected.image_limit ?? "Unlimited"} · Invoices:{" "}
                {selected.invoice_access ? "Yes" : "No"} · Messaging:{" "}
                {selected.messaging_access ? "Yes" : "No"}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              No payment is collected here. Your request is recorded as <strong>Pending</strong> and
              the Landoo team confirms it.
            </p>
            <div className="space-y-2">
              {plans.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No plans are configured for this service yet.
                </p>
              )}
              {plans.map((p) => (
                <label
                  key={p.id}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm ${
                    planId === p.id ? "border-brand bg-brand-soft/40" : "border-border"
                  }`}
                >
                  <input
                    type="radio"
                    name="plan"
                    className="mt-1"
                    checked={planId === p.id}
                    onChange={() => setPlanId(p.id)}
                  />
                  <span>
                    <span className="block font-semibold text-brown-dark">{p.plan_name}</span>
                    <span className="block text-[11px] text-muted-foreground">
                      {formatMoney(p.monthly_price)}/mo ·{" "}
                      {p.annual_price ? `${formatMoney(p.annual_price)}/yr · ` : ""}
                      {p.listing_limit ?? "Unlimited"} listings
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex gap-2">
              {(["monthly", "annual"] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCycle(c)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold capitalize ${
                    cycle === c ? "bg-brand text-brown-dark" : "bg-secondary text-muted-foreground"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              type="button"
              disabled={!selected || busy}
              onClick={() => selected && onSubmit(selected, cycle)}
              className="w-full rounded-full bg-brand px-4 py-2.5 text-sm font-semibold text-brown-dark shadow-soft disabled:opacity-60"
            >
              {busy
                ? "Submitting…"
                : dialog.kind === "renew"
                  ? "Request renewal"
                  : "Request upgrade"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-border/60 py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold capitalize text-brown-dark">{value}</span>
    </div>
  );
}
